import { Box, Link, Typography, Divider, styled, Stack } from "fashion/fabrics";
import BlogHeader from "nodes/blog/BlogHeader";
import CardButton from "nodes/core/CardButton";
import { POST_INDEX } from "names/blog";

const SectionStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
}));
const BlogPostStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
}));

const LINK_COMMON_DRAWING = "https://onecommon.cards/drawing";
const LINK_CONCEPT_MEMOIR = "https://concept.memoir.cards";
const LINK_VIDEO_EMBED =
  "https://www.youtube.com/embed/crl76RjGwtY?si=rd0bfQzEicLJT8vC";
const CARD_ID_CHALLENGE = "d2034cbd-1a7e-44f4-a8b3-8c9a983205f0";
const CARD_ID_DRAW_LEAF = "2af5cf42-ecc5-4300-a299-ccc0f978abf2";

const VideoIframe = styled("iframe")({
  width: "100%",
  border: "none",
  aspectRatio: "16 / 9", // Maintain 16:9 aspect ratio
});

const BlogPostDrawingChallengeCard = () => (
  <BlogPostStack>
    <BlogHeader postIndex={POST_INDEX.BLOG_DRAWING_CHALLENGE_CARD} />

    <SectionStack>
      <Typography>
        We're thrilled to announce our second community project:{" "}
        <b>the 7-Day Drawing Challenge: Capturing the Process!</b> ✏️
      </Typography>
      <Typography>
        For one week, immerse yourself in a daily drawing practice, focusing on
        a single model image (a "card model") and documenting its evolution.
        This challenge isn't just about the final product, it's about embracing
        the journey and later creating <b>your own challenge!</b>
      </Typography>

      <Typography component="ul">
        <li>
          Link to the <b>7 days drawing challenge:</b>{" "}
          <Link href={LINK_COMMON_DRAWING}>onecommon.cards/drawing</Link>
        </li>
        <li>
          Card Memoir concept web-app:{" "}
          <Link href={LINK_CONCEPT_MEMOIR}>concept.memoir.cards</Link>
        </li>
      </Typography>
    </SectionStack>
    <Divider variant="middle" />

    <SectionStack>
      <Typography variant="h4">Here's the Concept:</Typography>
      <Box display="flex" justifyContent="center">
        <CardButton cardId={CARD_ID_CHALLENGE} />
      </Box>

      <Typography component="ul">
        <li>
          <b>Choose your Card Model</b>: Discover the images to be your muse for
          the week!
        </li>
        <li>
          <b>Daily Drawing</b>: Dedicate one hour each day to drawing the card
          model.
        </li>
        <li>
          <b>Time-Lapse Art</b>: Capture your progress with four photos at
          ~15-minute intervals. Show us how your masterpiece unfolds!
        </li>
        <li>
          <b>Unleash Your Style</b>: Express yourself! Experiment with different
          mediums, techniques, and styles. There are no limits to your
          creativity.
        </li>
        <li>
          <b>Reflect & Grow</b>: At the end of each session, take a moment to
          reflect on your progress and challenges. Every step is a learning
          opportunity!
        </li>
        <li>
          <b>Share Your Journey</b>: Post your daily drawings and process photos
          using #CardMemoir. Let's inspire each other!
        </li>
      </Typography>
    </SectionStack>
    <Divider variant="middle" />

    <SectionStack>
      <Typography variant="h4">How to Share:</Typography>
      <Typography component="ol">
        <li>
          Get your drawing photos ready and navigate to:{" "}
          <Link href={LINK_COMMON_DRAWING}>onecommon.cards/drawing</Link>
        </li>
        <li>Watch the video to learn how to copy and edit a card:</li>
        <VideoIframe
          src={LINK_VIDEO_EMBED}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
        <li>
          Edit in graphic mode drag and <b>upload your 4 images</b> in the
          bottom tiles
        </li>
        <li>
          Edit the bottom left tile and add a <b>web-link & notes</b> to expand
          on the image
        </li>
        <li>
          Publish the card and share the link on your social platform with
          #CardMemoir tag
        </li>
      </Typography>
      <Box display="flex" justifyContent="center">
        <CardButton cardId={CARD_ID_DRAW_LEAF} />
      </Box>
    </SectionStack>

    <Divider variant="middle" />

    <SectionStack>
      <Typography variant="h4">Why participate?</Typography>
      <Typography component="ul">
        <li>
          <b>Build Consistency</b>: Develop a daily drawing habit and watch your
          skills flourish.
        </li>
        <li>
          <b>Find Your Voice</b>: Explore and refine your unique artistic style.
        </li>
        <li>
          <b>Track Your Progress</b>: Document your growth and see how far
          you've come.
        </li>
        <li>
          <b>Boost Your Confidence</b>: Celebrate your achievements, big or
          small.
        </li>
        <li>
          <b>Connect & Inspire</b>: Join a vibrant community of artists and
          share your passion!
        </li>
        <li>
          <b>Expand on the concept</b>: Create your own challenge with your own
          community
        </li>
      </Typography>
    </SectionStack>
  </BlogPostStack>
);

export default BlogPostDrawingChallengeCard;
